import React from 'react';
import {Alert, Box, CircularProgress, Stack, Typography} from '@mui/material';
import ReadonlyProseMirrorRenderer from './ReadonlyProseMirrorRenderer';
import SourcesPdfBboxViewer, {flattenModelOutput} from './SourcesPdfBboxViewer';
import SourcesFileProcessingProgress from './SourcesFileProcessingProgress';
import SourceFileDraftSelectionHost from './SourceFileDraftSelectionHost';
import {fetchSourceLayout} from './sourcesApi';

// Block-level MinerU types to show as overlay regions and allow navigation on.
// Sentence/line/noise types (ocr_text, page_footnote, span, line, …) are excluded —
// they clutter the overlay and break block-level navigation.
// Page furniture (footer, page_number, aside_text) IS included: MinerU 4.0.0 emits classification
// markings and marginalia as these types, and they appear in the derived content_list, so
// excluding them left the matching PM nodes unclickable in the text pane.
const BLOCK_LEVEL_TYPES = new Set([
    'text', 'title', 'header', 'section_header',
    'table', 'table_caption', 'table_footnote',
    'figure', 'figure_caption', 'image',
    'list', 'blockquote', 'code',
    'footnote', 'caption', 'equation', 'interline_equation',
    'abstract', 'reference', 'content',
    // MinerU 4.0.0 page furniture / marginalia
    'footer', 'page_number', 'aside_text', 'paragraph_title', 'sub_title', 'discarded',
    'page_footnote', 'chart', 'chart_caption', 'chart_footnote', 'image_caption',
    'image_footnote', 'algorithm', 'index',
]);

/**
 * Split-pane PDF ↔ document viewer.
 *
 *  ┌────────────────────────┬────────────────────────┐
 *  │  Extracted text (PM)   │  Original PDF + blocks │
 *  │  entity highlighting   │  model_output overlays │
 *  │  ← click syncs PDF     │  ← click syncs text    │
 *  └────────────────────────┴────────────────────────┘
 *
 * The vertical divider between the two panes is draggable.
 */

const MIN_PANE_PX = 120;
const DEFAULT_LEFT_PERCENT = 50;

/** Intersection-over-union of two [x0,y0,x1,y1] boxes; 0 when they don't overlap. */
function bboxIoU(a, b) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length < 4 || b.length < 4) return 0;
    const ix0 = Math.max(a[0], b[0]);
    const iy0 = Math.max(a[1], b[1]);
    const ix1 = Math.min(a[2], b[2]);
    const iy1 = Math.min(a[3], b[3]);
    const iw = ix1 - ix0;
    const ih = iy1 - iy0;
    if (iw <= 0 || ih <= 0) return 0;
    const inter = iw * ih;
    const areaA = Math.max(0, a[2] - a[0]) * Math.max(0, a[3] - a[1]);
    const areaB = Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);
    const union = areaA + areaB - inter;
    return union > 0 ? inter / union : 0;
}

// MinerU content_list bboxes are expressed on a fixed 0..1000 virtual-page scale (NOT the
// rendered page-image pixel size). model_output bboxes on the same page are already 0..1
// fractions, so dividing content_list coords by this constant lines the two spaces up.
const MINERU_CONTENT_LIST_SCALE = 1000;

/**
 * Normalise a content_list / PM bbox into the 0..1 space that model_output blocks use.
 *
 * MinerU emits two *different* bbox coordinate spaces in the same layout payload:
 *   • model_output (PDF pane) → already normalised fractions `[0..1, 0..1, 0..1, 0..1]`
 *   • content_list (PM pane)  → coords on a fixed 0..1000 virtual-page scale `[x0, y0, x1, y1]`
 * Comparing them directly always yields IoU 0, so we divide the content_list bbox by
 * {@link MINERU_CONTENT_LIST_SCALE} (1000) before any overlap test. This is independent of the
 * rendered page-image pixel size — earlier attempts that divided by the page's natural pixel
 * dimensions produced the wrong fractions and never overlapped.
 *
 * Returns the bbox unchanged when it already looks normalised (all components ≤ 1), or `null`
 * for malformed input.
 */
export function normalizeBbox(bbox) {
    if (!Array.isArray(bbox) || bbox.length < 4) return null;
    if (!bbox.every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
    const alreadyNormalized = bbox.every((v) => v >= 0 && v <= 1);
    if (alreadyNormalized) return bbox;
    const s = MINERU_CONTENT_LIST_SCALE;
    return [bbox[0] / s, bbox[1] / s, bbox[2] / s, bbox[3] / s];
}

/**
 * Resolve the PM-pane highlight key for a clicked PDF block.
 *
 * PDF blocks key off the model_output blockId (`p{page}_b{local}`), but a PM node only carries
 * that id when the server-side aligner matched it; unmatched nodes keep a content_list id
 * (`cl_p{page}_b{local}`) whose local index is a *different* numbering. So an exact key match
 * highlights aligned blocks, and for the rest we fall back to the best bbox overlap on the same
 * page. The PDF bbox is normalised (0..1) while PM bboxes use MinerU's 0..1000 content_list scale,
 * so we normalise each PM bbox via {@link normalizeBbox} before computing IoU. Returns the PM
 * node's `sourceBlockId` (or its positional index) so the PM renderer can highlight by exact key.
 */
export function resolvePmHighlightKey(pdfBlockKey, pdfBbox, pdfPageIndex, pmContent) {
    const topLevel = Array.isArray(pmContent?.content) ? pmContent.content : null;
    if (!topLevel) return pdfBlockKey;

    // 1) Exact id match (aligned blocks).
    const exactIdx = topLevel.findIndex((n) => n?.attrs?.sourceBlockId === pdfBlockKey);
    if (exactIdx !== -1) {
        return topLevel[exactIdx].attrs.sourceBlockId ?? exactIdx;
    }

    // 2) Best bbox overlap on the same page (unaligned / cl_-prefixed blocks).
    // pdfBbox is normalised 0..1; PM bboxes use the 0..1000 scale → normalise before IoU.
    const pdfNorm = normalizeBbox(pdfBbox);
    if (pdfNorm && pdfPageIndex != null) {
        let bestIdx = -1;
        let bestIoU = 0;
        topLevel.forEach((n, i) => {
            const a = n?.attrs;
            if (!a || a.pageIndex !== pdfPageIndex || !Array.isArray(a.bbox)) return;
            const pmNorm = normalizeBbox(a.bbox);
            if (!pmNorm) return;
            const iou = bboxIoU(pmNorm, pdfNorm);
            if (iou > bestIoU) {
                bestIoU = iou;
                bestIdx = i;
            }
        });
        if (bestIdx !== -1 && bestIoU > 0) {
            return topLevel[bestIdx].attrs?.sourceBlockId ?? bestIdx;
        }
    }

    // 3) No PM counterpart — fall back to the raw key (PM renderer scrolls by pageIndex instead).
    return pdfBlockKey;
}

/**
 * Resolve the PDF-pane (model_output) key for a clicked/hovered PM block — the inverse of
 * {@link resolvePmHighlightKey}. Prefers an exact `blockId` match; otherwise falls back to the
 * best bbox overlap on the same page (cl_-prefixed PM nodes have no matching model_output id).
 * PM bboxes use MinerU's 0..1000 content_list scale and model_output bboxes are already 0..1, so
 * the PM bbox is normalised via {@link normalizeBbox} before computing IoU. Returns the PDF
 * block's `blockId` (or `globalIdx`), or the raw PM key when no counterpart is found.
 */
export function resolvePdfKeyFromPm(pmBlockKey, pageIndex, pmContent, flatBlocks) {
    const exact = (flatBlocks || []).find((b) => b?.blockId === pmBlockKey);
    if (exact) return exact.blockId ?? exact.globalIdx;

    const pmNode = (Array.isArray(pmContent?.content) ? pmContent.content : [])
        .find((n) => n?.attrs?.sourceBlockId === pmBlockKey);
    const pmNorm = normalizeBbox(pmNode?.attrs?.bbox);
    if (pmNorm && pageIndex != null) {
        let best = null;
        let bestIoU = 0;
        (flatBlocks || []).forEach((b) => {
            if ((b?.page_idx ?? b?.pageIndex) !== pageIndex || !Array.isArray(b?.bbox)) return;
            // model_output bbox is already normalised 0..1.
            const iou = bboxIoU(b.bbox, pmNorm);
            if (iou > bestIoU) {
                bestIoU = iou;
                best = b;
            }
        });
        if (best && bestIoU > 0) return best.blockId ?? best.globalIdx;
    }
    return pmBlockKey;
}

/**
 * Returns a top-level PM node's bbox for `sourceBlockId`, normalised to 0..1 page fractions.
 * Used as a positional fallback when a PM node has no matching model_output block, so the PDF
 * pane can still scroll to the correct vertical position on the page.
 */
function pmBboxForKey(pmBlockKey, pmContent) {
    const nodes = Array.isArray(pmContent?.content) ? pmContent.content : [];
    const node = nodes.find((n) => n?.attrs?.sourceBlockId === pmBlockKey);
    return normalizeBbox(node?.attrs?.bbox);
}

/** Hook that returns the current width of a referenced container element. */
function useContainerWidth(ref) {
    const [width, setWidth] = React.useState(0);
    React.useEffect(() => {
        if (!ref.current) return;
        const ro = new ResizeObserver(([entry]) => {
            setWidth(entry.contentRect.width);
        });
        ro.observe(ref.current);
        setWidth(ref.current.getBoundingClientRect().width);
        return () => ro.disconnect();
    }, [ref]);
    return width;
}

const SourcesPdfDocumentSplitView = ({
    referenceId,
    userId,
    pmContent,
    isLoadingPm,
    pmError,
    processingStatus,
    anchorBlockKey = null,
    onBlockHighlight = null,
    highlightedSegmentIds = null,
    highlightedRanges = null,
    linkContext = null,
    searchMatches = null,
    activeSearchMatchIdx = -1,
    draftContext = null,
}) => {
    const [modelOutputPages, setModelOutputPages] = React.useState(null);
    const [isLoadingLayout, setIsLoadingLayout] = React.useState(false);
    const [layoutError, setLayoutError] = React.useState(null);
    // The two panes key off different id spaces (PM: sourceBlockId incl. cl_*; PDF: model_output
    // blockId), so each pane gets its own highlight key resolved from the click source.
    const [pmHighlightKey, setPmHighlightKey] = React.useState(null);
    const [pdfHighlightKey, setPdfHighlightKey] = React.useState(null);
    // Hover keys mirror the highlight keys but are *visual only* — they highlight the
    // corresponding block in the opposite pane on mouse-over WITHOUT scrolling, so the user
    // can correlate the two panes without losing their place.
    const [pmHoverKey, setPmHoverKey] = React.useState(null);
    const [pdfHoverKey, setPdfHoverKey] = React.useState(null);
    const [leftPercent, setLeftPercent] = React.useState(DEFAULT_LEFT_PERCENT);
    const containerRef = React.useRef(null);
    const isDragging = React.useRef(false);
    const containerWidth = useContainerWidth(containerRef);

    const pmRef = React.useRef(null);
    const pdfRef = React.useRef(null);

    const leftPx = containerWidth > 0
        ? Math.max(MIN_PANE_PX, Math.min(containerWidth - MIN_PANE_PX, (leftPercent / 100) * containerWidth))
        : null;

    const handleDividerMouseDown = React.useCallback((e) => {
        e.preventDefault();
        isDragging.current = true;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const onMouseMove = (moveEvent) => {
            if (!isDragging.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const rawPx = moveEvent.clientX - rect.left;
            const clampedPx = Math.max(MIN_PANE_PX, Math.min(rect.width - MIN_PANE_PX, rawPx));
            setLeftPercent((clampedPx / rect.width) * 100);
        };

        const onMouseUp = () => {
            isDragging.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    }, []);

    const flatBlocks = React.useMemo(
        () => flattenModelOutput(modelOutputPages),
        [modelOutputPages],
    );

    React.useEffect(() => {
        if (!referenceId || !userId) return;
        const controller = new AbortController();
        setIsLoadingLayout(true);
        setLayoutError(null);
        setModelOutputPages(null);

        fetchSourceLayout({referenceId, userId, signal: controller.signal})
            .then((data) => {
                const pages = data?.modelOutput;
                if (!Array.isArray(pages) || pages.length === 0) {
                    setModelOutputPages(null);
                    return;
                }
                // Keep only block-level types; filter out sentence/line/noise types
                // (ocr_text, page_number, page_footnote, etc.) that clutter the overlay
                // and make navigation confusing. Navigation works at block level only.
                const filtered = pages.map((page) =>
                    Array.isArray(page)
                        ? page.filter((b) => BLOCK_LEVEL_TYPES.has(String(b?.type ?? '').toLowerCase()))
                        : page,
                );
                setModelOutputPages(filtered);
            })
            .catch((err) => {
                if (err?.name === 'AbortError') return;
                console.warn('[SourcesPdfDocumentSplitView] Failed to load layout:', err.message);
                setLayoutError(err.message);
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsLoadingLayout(false);
            });

        return () => controller.abort();
    }, [referenceId, userId]);

    // PDF overlay clicked → highlight + scroll the PM pane to the matching block.
    // The PDF pane highlights the clicked block by its own blockId; the PM pane needs its own
    // key (exact sourceBlockId, else best bbox overlap on the same page) since unaligned PM
    // nodes carry cl_-prefixed ids whose local numbering differs from model_output.
    // Clicking the already-selected block toggles the selection off (clears both panes).
    const handleBboxClick = React.useCallback((blockKey, pageIndex) => {
        if (pdfHighlightKey != null && pdfHighlightKey === blockKey) {
            setPdfHighlightKey(null);
            setPmHighlightKey(null);
            return;
        }
        setPdfHighlightKey(blockKey);
        const pdfBlock = flatBlocks.find((b) => (b?.blockId ?? b?.globalIdx) === blockKey);
        const pmKey = resolvePmHighlightKey(
            blockKey, pdfBlock?.bbox, pageIndex, pmContent,
        );
        setPmHighlightKey(pmKey);
        pmRef.current?.scrollToBlock(pmKey, pageIndex);
    }, [flatBlocks, pmContent, pdfHighlightKey]);

    // PM block clicked → highlight + scroll the PDF pane to the matching block.
    // The PM pane highlights the clicked node by its own key; the PDF pane needs the model_output
    // blockId, resolved by exact id, else best bbox overlap on the same page (cl_-prefixed PM
    // nodes have no matching model_output blockId).
    // Clicking the already-selected block toggles the selection off (clears both panes).
    const handleBlockClick = React.useCallback((blockKey, pageIndex) => {
        if (pmHighlightKey != null && pmHighlightKey === blockKey) {
            setPmHighlightKey(null);
            setPdfHighlightKey(null);
            return;
        }
        setPmHighlightKey(blockKey);
        const pdfKey = resolvePdfKeyFromPm(blockKey, pageIndex, pmContent, flatBlocks);
        setPdfHighlightKey(pdfKey);
        // Pass the PM node's own bbox as a positional fallback: when `pdfKey` resolution falls
        // through (cl_-prefixed nodes with no model_output counterpart), the PDF pane can still
        // scroll to the right spot ON the page instead of snapping to the page top.
        pdfRef.current?.scrollToBlock(pdfKey, pageIndex, pmBboxForKey(blockKey, pmContent));
    }, [flatBlocks, pmContent, pmHighlightKey]);

    // ── Hover: cross-pane visual correlation only (no scroll) ────────────────
    // Hovering a PM block lights up the matching PDF block (and vice-versa) so the user can see
    // the correspondence without navigating. We resolve the opposite-pane key the same way clicks
    // do, but never call scrollToBlock.
    const handleBlockHover = React.useCallback((blockKey, pageIndex) => {
        if (blockKey == null) { setPmHoverKey(null); setPdfHoverKey(null); return; }
        setPmHoverKey(blockKey);
        setPdfHoverKey(resolvePdfKeyFromPm(blockKey, pageIndex, pmContent, flatBlocks));
    }, [flatBlocks, pmContent]);

    const handleBboxHover = React.useCallback((blockKey, pageIndex) => {
        if (blockKey == null) { setPmHoverKey(null); setPdfHoverKey(null); return; }
        setPdfHoverKey(blockKey);
        const pdfBlock = flatBlocks.find((b) => (b?.blockId ?? b?.globalIdx) === blockKey);
        setPmHoverKey(resolvePmHighlightKey(blockKey, pdfBlock?.bbox, pageIndex, pmContent));
    }, [flatBlocks, pmContent]);

    const hasBlocks = flatBlocks.length > 0;

    // Resolve the deep-link anchor (a PM top-level node index from sentenceHit.contentPath[0])
    // to the node's stable cross-pane keys. The search anchor is a *positional* PM index, but
    // the PDF pane keys off the server-stamped `sourceBlockId` (p{page}_b{local}) and falls back
    // to `pageIndex`. Resolving here lets us highlight/scroll BOTH panes consistently instead of
    // pushing a raw numeric index that the PDF pane can never match.
    const anchorResolved = React.useMemo(() => {
        if (anchorBlockKey == null) return null;
        const topLevel = Array.isArray(pmContent?.content) ? pmContent.content : null;
        const node = topLevel && typeof anchorBlockKey === 'number'
            ? topLevel[anchorBlockKey]
            : null;
        return {
            blockIndex: anchorBlockKey,
            sourceBlockId: node?.attrs?.sourceBlockId ?? null,
            pageIndex: node?.attrs?.pageIndex ?? null,
        };
    }, [anchorBlockKey, pmContent]);

    // M6 deep link: when opened from a federated-search hit, highlight + scroll both panes
    // to the hit's block once content is ready. Re-arms when a different anchor arrives.
    const anchoredForKeyRef = React.useRef(null);
    React.useEffect(() => {
        if (!anchorResolved) {
            anchoredForKeyRef.current = null;
            return;
        }
        if (!pmContent || isLoadingPm) {
            return;
        }
        if (anchoredForKeyRef.current === anchorResolved.blockIndex) {
            return;
        }
        anchoredForKeyRef.current = anchorResolved.blockIndex;

        const {blockIndex, sourceBlockId, pageIndex} = anchorResolved;
        // PM pane highlights/scrolls by positional index (its blocks expose data-block-index);
        // the PDF pane needs the stable sourceBlockId (with pageIndex as a page-level fallback).
        setPmHighlightKey(sourceBlockId ?? blockIndex);
        setPdfHighlightKey(sourceBlockId ?? blockIndex);
        // Defer to let the PM/PDF renderers commit their block DOM before scrolling.
        const raf = requestAnimationFrame(() => {
            pmRef.current?.scrollToBlock(blockIndex, pageIndex);
            pdfRef.current?.scrollToBlock(
                sourceBlockId ?? blockIndex,
                pageIndex,
                pmBboxForKey(sourceBlockId, pmContent),
            );
        });
        return () => cancelAnimationFrame(raf);
    }, [anchorResolved, pmContent, isLoadingPm]);

    return (
        <Box ref={containerRef} sx={{display: 'flex', height: '100%', overflow: 'hidden', position: 'relative'}}>
            {/* ── Left: Extracted text (ProseMirror) ── */}
            <Box sx={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                ...(leftPx !== null ? {width: leftPx} : {flex: 1}),
            }}>
                <Box sx={(theme) => ({
                    px: 1.5, py: 0.75, flexShrink: 0,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.paper',
                })}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Extracted text
                    </Typography>
                    {hasBlocks && (
                        <Typography variant="caption" color="text.secondary" sx={{ml: 1}}>
                            · click a block to highlight in PDF
                        </Typography>
                    )}
                </Box>

                <SourceFileDraftSelectionHost draftContext={draftContext} sx={{flex: 1, overflowY: 'auto', minHeight: 0}}>
                    <Box sx={{p: 2, minHeight: 0}}>
                        {processingStatus?.kind === 'processing' ? (
                            <SourcesFileProcessingProgress
                                status={processingStatus}
                                title="Extracting text…"
                                compact
                            />
                        ) : isLoadingPm ? (
                            <Stack alignItems="center" justifyContent="center" flex={1} spacing={1.5} p={3}>
                                <CircularProgress size={24}/>
                                <Typography variant="body2" color="text.secondary">Loading extracted text…</Typography>
                            </Stack>
                        ) : pmError ? (
                            <Alert severity="error" sx={{m: 2}}>{pmError}</Alert>
                        ) : pmContent ? (
                            <ReadonlyProseMirrorRenderer
                                ref={pmRef}
                                content={pmContent}
                                highlightedBlockKey={pmHighlightKey}
                                hoverBlockKey={pmHoverKey}
                                onBlockClick={hasBlocks ? handleBlockClick : undefined}
                                onBlockHover={hasBlocks ? handleBlockHover : undefined}
                                highlightedSegmentIds={highlightedSegmentIds}
                                highlightedRanges={highlightedRanges}
                                onBlockHighlight={onBlockHighlight}
                                linkContext={linkContext}
                                searchMatches={searchMatches}
                                activeSearchMatchIdx={activeSearchMatchIdx}
                            />
                        ) : (
                            <Stack alignItems="center" justifyContent="center" flex={1} p={3}>
                                <Typography variant="body2" color="text.secondary">
                                    Extracted text not available yet.
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                </SourceFileDraftSelectionHost>
            </Box>

            {/* Draggable vertical divider */}
            <Box
                onMouseDown={handleDividerMouseDown}
                sx={(theme) => ({
                    flexShrink: 0,
                    width: 5,
                    cursor: 'col-resize',
                    bgcolor: theme.palette.divider,
                    position: 'relative',
                    zIndex: 10,
                    transition: 'background-color 0.15s',
                    '&:hover': {
                        bgcolor: theme.palette.primary.main,
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        height: 32,
                        width: 3,
                        borderRadius: 4,
                        bgcolor: 'rgba(255,255,255,0.55)',
                        pointerEvents: 'none',
                    },
                })}
            />

            {/* ── Right: Original PDF with bbox overlays ── */}
            <Box sx={{flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                <Box sx={(theme) => ({
                    px: 1.5, py: 0.75, flexShrink: 0,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.paper',
                })}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Layout PDF
                    </Typography>
                    {isLoadingLayout && (
                        <Typography variant="caption" color="text.secondary" sx={{ml: 1}}>· loading blocks…</Typography>
                    )}
                    {!isLoadingLayout && hasBlocks && (
                        <Typography variant="caption" color="text.secondary" sx={{ml: 1}}>
                            · {flatBlocks.length} blocks · click a block to highlight in text
                        </Typography>
                    )}
                    {!isLoadingLayout && layoutError && (
                        <Typography variant="caption" color="warning.main" sx={{ml: 1}}>
                            · no block data (reprocess to enable overlays)
                        </Typography>
                    )}
                </Box>

                {isLoadingLayout ? (
                    <Stack flex={1} alignItems="center" justifyContent="center" spacing={1.5}>
                        <CircularProgress size={28}/>
                        <Typography variant="body2" color="text.secondary">Loading PDF layout…</Typography>
                    </Stack>
                ) : (
                    <SourcesPdfBboxViewer
                        ref={pdfRef}
                        referenceId={referenceId}
                        userId={userId}
                        modelOutputPages={modelOutputPages}
                        highlightedBlockKey={pdfHighlightKey}
                        hoverBlockKey={pdfHoverKey}
                        onBlockClick={handleBboxClick}
                        onBlockHover={handleBboxHover}
                    />
                )}
            </Box>
        </Box>
    );
};

export default SourcesPdfDocumentSplitView;
